import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIsFocused, useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import { ActivityIndicator, Pressable, RefreshControl, ScrollView, useColorScheme } from 'react-native';
import { Swipeable } from 'react-native-gesture-handler';
import { View, XStack, YStack } from 'tamagui';
// Lucide equivalents of the previously hand-rolled single-glyph
// components (aliased to the old names so call sites keep reading as
// the app's icon vocabulary). Check/X keep their old 2.5 stroke at the
// call site below; Zap is passed fill to stay the filled "spark" glyph
// the founder picked ("there's a spark") -- a stroked outline read too
// faint at its 10px size.
import { Check as CheckIcon, X as XIcon, Zap as LightningIcon } from 'lucide-react-native';

import { Text } from '@/components/text';
import { Badge } from '@/components/badge';
import { SolidSurface } from '@/components/glass-surface';
import { LoadingIndicator } from '@/components/loading-indicator';
import { PrimaryButton } from '@/components/primary-button';
import { PseudonymousAvatar } from '@/components/pseudonymous-avatar';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { ScreenHeader } from '@/components/screen-header';
import { StaleRefreshText } from '@/components/resource-list-view';
import { EmptyState } from '@/components/empty-state';
import { PageEnd } from '@/components/page-end';
import { Subheading } from '@/components/subheading';
import { TextLink } from '@/components/text-link';
import { WebRefreshButton } from '@/components/web-refresh';
import { useAuth } from '@/hooks/auth-context';
import { useAllConnections } from '@/hooks/use-connections';
import { useMyRevealRequests } from '@/hooks/use-my-reveal-requests';
import { useProfile } from '@/hooks/use-profile';
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion';
import { api, type RevealRequest } from '@/lib/api';
import { displayHandle } from '@/lib/avatar-identity';
import { pullToRefreshHaptic } from '@/lib/haptics';
import { queryKeys } from '@/lib/query-keys';
import { describeCooldown, describeRevealRequestStatus } from '@/lib/reveal-request-status';
import { getSeenSafetyReminderConnectionIds } from '@/lib/safety-reminder-seen';
import { paginatedListState } from '@/lib/list-state';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';
import { Toast } from '@/components/toast';

// Issue #253: the swipe-accept/decline gesture (IncomingRequestRow) and
// "Revoke connection & block" (ConnectionRow) are both real, hard-to-
// reverse state changes with no confirmation step -- a deliberate
// design choice (design.md's "no dark patterns": a redundant "are you
// sure?" modal on an already-deliberate action is friction, not safety)
// that the 2026-09-16 critique correctly pointed out still leaves the
// LOW-intentionality swipe path with zero error recovery. The fix kept
// here is a brief undo window, not a confirmation dialog: the action is
// scheduled, not fired, and only actually committed (the real mutation
// call) after this many ms with no "Undo" tap. 5s matches the
// "~4-6s window" the critique itself specified.
const UNDO_WINDOW_MS = 5000;

// A single toast slot for the whole screen (not per-row) -- this app's
// own Toast component deliberately has no queueing system yet (see its
// own doc comment), and one undo affordance visible at a time is
// standard snackbar behavior anyway. A second undoable action started
// while one is already showing simply replaces the visible toast; the
// FIRST action's own commit timer keeps running in its row regardless
// (each row owns its own timer independently -- this state only
// controls what's currently displayed), so it is never silently lost,
// only no longer the one with a visible Undo button.
type PendingUndo = { message: string; onUndo: () => void };

// The only screen in the app where a reveal request (incoming, to
// accept/decline) or an existing connection (to revoke) can be found
// -- issue #16's core mechanic, served by Task 1's two new list
// endpoints. Accepting hands off to the mandatory safety-reminder
// screen (Task 7); nothing else in this screen writes identity data
// itself.
//
// Retrofit (issue #123): this screen predated the PR #69 list pattern
// and was never caught up -- list errors rendered as "No pending
// requests" over invisible failures, there was no pull-to-refresh, and
// the five content sections had no scroll container, so everything
// below the fold (including the Connections section's revoke control)
// was unreachable on a short screen. The row components are also
// module-level now (issue #99): nested inside RevealRequests they were
// re-created on every parent render, remounting each row and dropping
// in-flight mutation state whenever either list query updated.
export function RevealRequests() {
  const router = useRouter();
  const auth = useAuth();
  const currentUserId = auth.status === 'signed-up' ? auth.user.id : undefined;

  const requests = useMyRevealRequests();
  const connections = useAllConnections();
  const seenSafetyReminders = useQuery({
    queryKey: queryKeys.safetyReminderSeen(),
    queryFn: getSeenSafetyReminderConnectionIds,
  });

  const incoming = useMemo(
    () => (requests.data ?? []).filter((rr) => rr.status === 'requested' && rr.recipientId === currentUserId),
    [requests.data, currentUserId],
  );
  const outgoing = useMemo(
    () => (requests.data ?? []).filter((rr) => rr.status === 'requested' && rr.requesterId === currentUserId),
    [requests.data, currentUserId],
  );
  // Requests *this user sent* that were declined -- the only case
  // describeCooldown's wait-time is meaningful for. A request the
  // current user *received* and declined has no cooldown to show
  // them: the 24h cooldown (reveal_request.go's declineCooldown) gates
  // the requester re-requesting the same recipient, not the recipient
  // who did the declining. Separate section, not folded into
  // OutgoingRequestRow's "Sent by you" list above, since that list is
  // scoped to status === 'requested' and a declined request is a
  // different (resolved, past-tense) thing to show.
  const recentlyDeclined = useMemo(
    () => (requests.data ?? []).filter((rr) => rr.status === 'declined' && rr.requesterId === currentUserId),
    [requests.data, currentUserId],
  );

  // Requests this user SENT that are now mutual, whose safety reminder
  // this device hasn't shown yet -- issue #103. The recipient sees the
  // reminder unconditionally as the direct result of their own Accept
  // tap (IncomingRequestRow below); the requester has no equivalent
  // action to hang it off, since accept is unilateral by the recipient,
  // so this is the one place they'd otherwise discover a new connection
  // without ever seeing the "meet in public first" copy design.md
  // requires. Rendered above every other section (including incoming
  // requests) since it's the one thing here that's a safety requirement
  // rather than routine list content.
  const newlyMutualForRequester = useMemo(
    () =>
      (requests.data ?? []).filter(
        (rr) =>
          rr.status === 'mutual' &&
          rr.requesterId === currentUserId &&
          rr.connectionId &&
          !(seenSafetyReminders.data ?? new Set<string>()).has(rr.connectionId),
      ),
    [requests.data, currentUserId, seenSafetyReminders.data],
  );

  // Issue #123: a list failure with no cached data must NOT render the
  // per-section "No pending requests" copies -- "feedback may not
  // outrun the truth" (this screen's original bug was exactly that, a
  // 401/500 masquerading as an empty inbox). With cached data the
  // sections still render and the failure shows as the same
  // stale-refresh notice the Threads screen uses.
  // All-or-nothing on purpose (review pass on #123): a partial first
  // load (requests OK, connections failed) must not render the failed
  // half's sections -- "(connections.data ?? [])" would show a false
  // "No connections yet" under the error notice. Full error + retry
  // refetches both; once cached, failures degrade to the stale notice.
  // The whole-screen state comes from the shared derivation
  // (lib/list-state.ts) over the two queries combined; the sections
  // own their own empties ("No pending requests.", "No connections
  // yet."), so itemCount only needs to stay nonzero.
  // Offline, a first fetch sits paused in `pending` forever; treat that as the
  // error state (with Try again) instead of an endless spinner.
  const pausedWithoutData = [requests, connections].some((q) => q.isPending && q.fetchStatus === 'paused');
  const listState = paginatedListState({
    isPending: !pausedWithoutData && (requests.isPending || connections.isPending),
    isError: pausedWithoutData || requests.isError || connections.isError,
    hasData: requests.data !== undefined && connections.data !== undefined,
    itemCount: 1,
  });

  const refreshing = requests.isRefetching || connections.isRefetching;
  const isAllEmpty =
    newlyMutualForRequester.length + incoming.length + outgoing.length + recentlyDeclined.length === 0 &&
    (connections.data ?? []).length === 0;

  const [undoToast, setUndoToast] = useState<PendingUndo | null>(null);
  const showUndoToast = (message: string, onUndo: () => void) => setUndoToast({ message, onUndo });
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();

  const refresh = () => {
    pullToRefreshHaptic();
    void requests.refetch();
    void connections.refetch();
  };

  // Native semantic color, not a Tamagui token -- RefreshControl is a
  // real native view, same reasoning as the Threads screen's own
  // RefreshControl.
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  return (
    <ScreenContainer scrollUnderDock>
      <YStack flex={1}>
        {/* Refresh moved inline next to Back (issue #290) -- it used to
            be its own standalone row below Back. */}
        <YStack>
          <ScreenHeader
            action={
              <WebRefreshButton
                onRefresh={() => {
                  void requests.refetch();
                  void connections.refetch();
                }}
                refreshing={requests.isRefetching || connections.isRefetching}
              />
            }
            onBack={() => router.back()}
          />
        </YStack>

        {listState === 'pending' ? (
          <View flex={1} alignItems="center" justifyContent="center">
            <LoadingIndicator />
          </View>
        ) : listState === 'error' ? (
          <YStack flex={1} alignItems="center" justifyContent="center" gap="$3">
            <Subheading color="$color" textAlign="center">
              Couldn&apos;t load reveal requests
            </Subheading>
            <Text color="$colorSecondary" textAlign="center">
              Check your connection and try again.
            </Text>
            <PrimaryButton onPress={() => refresh()}>Try again</PrimaryButton>
          </YStack>
        ) : isAllEmpty ? (
          <EmptyState
            ring
            title="No reveal requests"
            description="When a neighbor wants to swap identities with you, it shows up here. Replying on a thread is how that starts."
            action={{ label: 'Browse nearby threads', onPress: () => router.push('/') }}
          />
        ) : (
          <ScrollView
            style={{ flex: 1 }}
            {...dockScroll}
            // Extra bottom padding while the undo toast is showing
            // (issue #253 review) -- it's absolutely positioned outside
            // the scroll content's own layout flow, so without this the
            // last row's own interactive content (e.g. a ConnectionRow's
            // "Revoke connection & block" button) can end up covered by
            // it, an 2.4.11-style "obscured on focus/interaction"
            // problem for whatever's at the bottom of a short list.
            // ~96px approximates one toast's rendered height (message +
            // action button + padding) -- not measured live, since
            // Toast has no onLayout callback to measure it with and
            // adding one for a single call site is more machinery than
            // a fixed estimate with headroom warrants.
            contentContainerStyle={{
              gap: space[5],
              paddingBottom: (undoToast ? space[6] + space[16] + space[8] : space[6]) + dockClearance,
            }}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={refresh}
                colors={[colors.actionPrimary]}
                tintColor={colors.actionPrimary}
                progressBackgroundColor={colors.surfaceCard}
              />
            }
          >
            {listState === 'stale' && <StaleRefreshText />}

            {newlyMutualForRequester.length > 0 && (
              <YStack gap="$2">
                <Subheading color="$color">New connection</Subheading>
                {newlyMutualForRequester.map((rr) => (
                  <NewConnectionRow key={rr.id} request={rr} />
                ))}
              </YStack>
            )}

            <YStack gap="$2">
              <Subheading color="$color">Requests to you</Subheading>
              {incoming.length === 0 ? (
                <Text color="$colorTertiary">No pending requests.</Text>
              ) : (
                incoming.map((rr) => <IncomingRequestRow key={rr.id} request={rr} onShowUndoToast={showUndoToast} />)
              )}
            </YStack>

            <YStack gap="$2">
              <Subheading color="$color">Requests you sent</Subheading>
              {outgoing.length === 0 ? (
                <Text color="$colorTertiary">No pending requests.</Text>
              ) : (
                outgoing.map((rr) => <OutgoingRequestRow key={rr.id} request={rr} currentUserId={currentUserId} />)
              )}
            </YStack>

            {recentlyDeclined.length > 0 && (
              <YStack gap="$2">
                <Subheading color="$color">Recently declined</Subheading>
                {recentlyDeclined.map((rr) => (
                  <DeclinedRequestRow key={rr.id} request={rr} currentUserId={currentUserId} />
                ))}
              </YStack>
            )}

            <YStack gap="$2">
              <Subheading color="$color">Connections</Subheading>
              {(connections.data ?? []).length === 0 ? (
                <Text color="$colorTertiary">No connections yet.</Text>
              ) : (
                (connections.data ?? []).map((c) => (
                  <ConnectionRow
                    key={c.id}
                    connectionId={c.id}
                    otherUserId={c.otherUserId}
                    otherUserHandle={c.otherUserHandle}
                    onShowUndoToast={showUndoToast}
                  />
                ))
              )}
            </YStack>
            <PageEnd>That&apos;s everything. Requests land here the moment they arrive.</PageEnd>
          </ScrollView>
        )}

        {/* Single undo snackbar for the whole screen (issue #253) --
            absolutely positioned against the outer YStack (RN's default
            position:relative on every View means this needs no extra
            prop), floating over content rather than pushing it, which
            is standard snackbar placement and keeps the list's own
            scroll position stable when it appears. the dock clearance clears
            the home indicator the same way ScreenContainer's own
            content padding does; this overlay sits outside that
            padding flow, so it has to apply the inset itself. */}
        {undoToast && (
          <View position="absolute" left={space[4]} right={space[4]} bottom={dockClearance + space[4]}>
            <Toast
              variant="info"
              message={undoToast.message}
              durationMs={UNDO_WINDOW_MS}
              onDismiss={() => setUndoToast(null)}
              action={{
                label: 'Undo',
                onPress: () => {
                  undoToast.onUndo();
                  setUndoToast(null);
                },
              }}
            />
          </View>
        )}
      </YStack>
    </ScreenContainer>
  );
}

// Issue #117: consent must not be blind. The requester's profile
// (avatar + handle + a tappable link through to the full view) is
// fetched per card so the recipient can see who is asking BEFORE
// accepting -- previously this card rendered zero identity context and
// the only look happened after the fact. One query per card is fine at
// this screen's scale: incoming requests are rate-limited to one per
// (requester, recipient) pair per 24h, and TanStack dedupes by key with
// the profile view's own query.
// Founder's adversarial-lens audit, 2026-09-16: "Zero swipe gestures
// anywhere in the app, despite Tinder/Bumble/Hinge training an entire
// generation that swipe = the primary verb of this category... Reveal
// requests are accept/decline BUTTONS -- a completely static, form-
// like decision." Swipe right (the same direction as a Tinder "like")
// accepts; swipe left declines -- the existing PrimaryButtons below
// stay exactly as they were, not replaced: a swipe-only accept/decline
// would fail the same WCAG 2.5.7 (Dragging Movements) requirement the
// photo grid's own drag-to-reorder already had to solve (profile/
// index.tsx's PhotoSection) -- a decision this consequential (this
// literally creates a real-world connection) needs a real, non-drag
// path for anyone who can't perform the gesture, not just a nice-to-
// have. Swipeable is gesture-handler's own primitive (already a real
// dependency via the photo grid's own drag library), not hand-rolled.
//
// Issue #253: only the SWIPE path defers to the undo window below --
// the Accept/Decline PrimaryButtons keep their original immediate-
// commit behavior. The critique that opened #253 was specifically
// about the swipe gesture's low intentionality ("a grazing swipe while
// scrolling"); a deliberate, labeled button tap is not that same risk,
// and giving it the same defer-and-toast treatment would just add
// latency to an already-considered action with no real safety upside.
function IncomingRequestRow({
  request,
  onShowUndoToast,
}: {
  request: RevealRequest;
  onShowUndoToast: (message: string, onUndo: () => void) => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const swipeableRef = useRef<Swipeable>(null);
  // Set the moment a swipe crosses threshold, cleared only by Undo --
  // NOT on unmount (see swipeCommitTimeoutRef below): once armed, the
  // action commits unless the user explicitly undoes it, the same
  // semantics as any other "Undo" snackbar (closing the screen isn't
  // an undo).
  const [pendingSwipeAction, setPendingSwipeAction] = useState<'accept' | 'decline' | null>(null);
  const swipeCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read in accept's onSuccess below, which can fire well after this
  // row (or the whole screen) has unmounted -- issue #253 review: since
  // the commit timer deliberately isn't tied to component lifecycle,
  // the mandatory safety-reminder redirect must not fire onto whatever
  // OTHER screen the user has since navigated to. useIsFocused's return
  // value alone goes stale the moment this component unmounts (React
  // stops re-rendering it), so it's mirrored into a ref that the
  // deferred callback can still read.
  const isFocused = useIsFocused();
  const isFocusedRef = useRef(isFocused);
  useEffect(() => {
    isFocusedRef.current = isFocused;
  }, [isFocused]);
  // Resolved color for the Accept/Decline icons -- react-native-svg's
  // stroke prop isn't part of Tamagui's styling system, same reasoning
  // as every other icon call site in this app (see glass-surface.tsx's
  // useGlassAccentColor doc comment).
  const iconColorScheme = useColorScheme();
  const colors = iconColorScheme === 'dark' ? darkColors : lightColors;

  const requesterProfile = useProfile(request.requesterId);
  const requesterHandle = requesterProfile.data?.handle;

  const accept = useMutation({
    mutationFn: () => api.acceptRevealRequest(request.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.revealRequests() });
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      // The Profile tab's badge rides the /me/activity poll now (issue
      // #395); invalidate it alongside so the badge drops immediately.
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      // Mandatory safety-reminder screen at the moment of mutual
      // accept -- issue #16's own hard requirement, must not be
      // skipped or deferred.
      //
      // Implementation scoping (issue #253 review): with the swipe path
      // now deferred behind a 5s undo window, this onSuccess can fire
      // well after the user has navigated to a completely different
      // screen -- an unconditional router.push here would yank them
      // into /safety-reminder mid-task on whatever they're now doing,
      // which is a worse failure mode than the redirect this guard
      // skips. Only push while this row's screen is still focused; the
      // recipient side has no existing "reminder not yet shown"
      // fallback the way the requester side's newlyMutualForRequester
      // does (issue #103), so the rare case of navigating away inside
      // the 5s window means this device won't show the reminder for
      // this connection at all, not just late. Narrow, accepted gap --
      // building the requester-side's persistent fallback for the
      // recipient too is real scope beyond this issue's undo-affordance
      // ask, not a one-line fix.
      if (isFocusedRef.current) {
        router.push({ pathname: '/safety-reminder', params: { otherUserId: request.requesterId } });
      }
    },
  });
  const decline = useMutation({
    mutationFn: () => api.declineRevealRequest(request.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.revealRequests() });
      // Same badge-drop-now reasoning as accept's onSuccess above.
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      // A decline can auto-create a Block server-side once past the
      // backend's decline threshold (DeclineRevealRequestHandler);
      // the client can't see whether THIS decline crossed it, so
      // invalidate unconditionally rather than guess.
      queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      queryClient.invalidateQueries({ queryKey: queryKeys.thread() });
    },
  });

  // Arms the undo window instead of mutating immediately -- called only
  // from the swipe gesture below, never from the buttons (see this
  // function's own doc comment above the component).
  function scheduleSwipeAction(action: 'accept' | 'decline') {
    if (pendingSwipeAction) return; // already committed to an action this session
    setPendingSwipeAction(action);
    swipeCommitTimeoutRef.current = setTimeout(() => {
      (action === 'accept' ? accept : decline).mutate();
    }, UNDO_WINDOW_MS);
    onShowUndoToast(action === 'accept' ? 'Accepted' : 'Declined', () => {
      if (swipeCommitTimeoutRef.current) clearTimeout(swipeCommitTimeoutRef.current);
      setPendingSwipeAction(null);
    });
  }

  // Row disappears the instant the swipe commits to an action -- the
  // toast (with Undo) is the only visible trace until either Undo puts
  // it back or the real mutation succeeds and this request drops out of
  // the parent's `incoming` list entirely. Same "optimistic hide, toast
  // is the record" pattern as ConnectionRow below.
  if (pendingSwipeAction) return null;

  return (
    <Swipeable
      ref={swipeableRef}
      overshootLeft={false}
      overshootRight={false}
      leftThreshold={80}
      rightThreshold={80}
      renderLeftActions={() => (
        <XStack flex={1} backgroundColor="$feedbackSuccessBg" borderRadius="$lg" alignItems="center" pl="$4" mr="$2">
          <Text color="$feedbackSuccessText" fontWeight="$semibold">
            Accept
          </Text>
        </XStack>
      )}
      renderRightActions={() => (
        <XStack
          flex={1}
          backgroundColor="$feedbackErrorBg"
          borderRadius="$lg"
          alignItems="center"
          justifyContent="flex-end"
          pr="$4"
          ml="$2"
        >
          <Text color="$feedbackErrorText" fontWeight="$semibold">
            Decline
          </Text>
        </XStack>
      )}
      onSwipeableWillOpen={(direction) => {
        if (accept.isPending || decline.isPending || pendingSwipeAction) return;
        void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        scheduleSwipeAction(direction === 'left' ? 'accept' : 'decline');
        swipeableRef.current?.close();
      }}
    >
      <SolidSurface style={{ padding: space[4] }}>
        <YStack gap="$3">
          <XStack alignItems="center" gap="$3">
            <PseudonymousAvatar handle={requesterHandle} id={request.requesterId} size={40} />
            <YStack flexShrink={1} gap="$1">
              {requesterProfile.isError ? (
                // A failed fetch must not silently become blind consent:
                // say the identity couldn't load and offer the retry, but
                // keep Accept reachable -- a 404 (requester deleted their
                // account mid-request) is a real state and must not block
                // the recipient's own decision forever.
                <>
                  <Text color="$color" fontWeight="$semibold">
                    Couldn&apos;t load who&apos;s asking.
                  </Text>
                  <TextLink
                    alignSelf="flex-start"
                    fontSize="$meta"
                    lineHeight="$meta"
                    onPress={() => requesterProfile.refetch()}
                  >
                    Tap to try again
                  </TextLink>
                </>
              ) : (
                <>
                  <Text color="$color" fontWeight="$semibold">
                    {requesterHandle
                      ? `${displayHandle(requesterHandle)} wants to reveal identities with you.`
                      : 'Someone nearby wants to reveal identities with you.'}
                  </Text>
                  <TextLink
                    alignSelf="flex-start"
                    fontSize="$meta"
                    lineHeight="$meta"
                    onPress={() =>
                      router.push({ pathname: '/users/[userId]', params: { userId: request.requesterId } })
                    }
                  >
                    View their profile before deciding
                  </TextLink>
                </>
              )}
            </YStack>
          </XStack>
          {(accept.isError || decline.isError) && (
            <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
              {accept.error?.message ||
                decline.error?.message ||
                'Something went wrong. Check your connection and try again.'}
            </Text>
          )}
          {/* Founder, 2026-09-16: "red and red feels a bit clunky for
              the matches? Shouldn't we have ticks or cross with
              different colours?" -- this app's own brand-primary color
              IS a warm red (tokens.generated.ts's actionPrimary), which
              reads as visually close to $destructive's red at a
              glance. Accept stays on $primary (this app's one
              consistent affirmative-action variant -- recoloring it
              green just for this screen would break "one action role,
              one variant" everywhere else Accept-like CTAs appear), but
              a checkmark/X icon gives a real shape differentiator that
              doesn't depend on the two reds reading apart. */}
          <XStack gap="$3">
            <PrimaryButton
              size="sm"
              loading={accept.isPending}
              onPress={() => {
                if (!accept.isPending && !decline.isPending) accept.mutate();
              }}
            >
              {accept.isPending ? (
                <ActivityIndicator color="white" />
              ) : (
                <XStack alignItems="center" gap="$2">
                  <CheckIcon size={16} strokeWidth={1.75} color={colors.textOnAction} />
                  <Text color="$colorOnAction" fontWeight="$semibold">
                    Accept
                  </Text>
                </XStack>
              )}
            </PrimaryButton>
            <PrimaryButton
              size="sm"
              variant="outline"
              loading={decline.isPending}
              onPress={() => {
                if (!accept.isPending && !decline.isPending) decline.mutate();
              }}
            >
              {decline.isPending ? (
                <ActivityIndicator color="white" />
              ) : (
                <XStack alignItems="center" gap="$2">
                  <XIcon size={16} strokeWidth={1.75} color={colors.textPrimary} />
                  <Text color="$color" fontWeight="$semibold">
                    Decline
                  </Text>
                </XStack>
              )}
            </PrimaryButton>
          </XStack>
        </YStack>
      </SolidSurface>
    </Swipeable>
  );
}

// A request this user sent that's now mutual and whose safety reminder
// this device hasn't shown yet -- see RevealRequests's
// newlyMutualForRequester memo and safety-reminder/index.tsx's doc
// comment for the full rationale (issue #103). Tapping through is the
// only way to clear this row: connectionId travels to the route so
// "Got it" there can mark it seen, matching the mandatory,
// un-skippable nature of the recipient-side reminder -- this is the
// requester's equivalent of that one-time moment, not routine list
// content.
// Founder critique, 2026-09-16 ("the entire app needs to feel modern"),
// applied here as an /impeccable delight pass: this row is the single
// biggest positive milestone in the whole product (design.md's own
// pitch -- "when there's something there, for both of you, you can
// choose to share who you really are"), a mutual reveal-consent match.
// It used to render in `$feedbackWarning*` tokens -- literally the
// same visual language as an error/caution banner -- which is a real
// token-by-intent mismatch, not just a missed decoration: this is a
// SUCCESS state (a completed, positive outcome), and the still-
// required next step (read the safety reminder) is routine guidance,
// not itself a warning. Re-skinned to this app's own SolidSurface
// material (the same signature surface every other card in this pass
// now uses) and `$feedbackSuccess*` tokens, with a small check-badge
// on the avatar -- a checkmark, not a heart, for the same reason
// matches-icon.tsx already documents (this is a neighbor-focused
// product, not a dating app; "confirmed/complete" is the accurate
// read, not "romantic"). Copy is untouched -- already-reviewed text
// (issue #116's own tightening), not this pass's job to rewrite.
// This row only ever renders for a connection this REQUESTER hasn't
// acknowledged yet (see the `newlyMutualForRequester` filter above,
// which excludes anything already in getSeenSafetyReminderConnectionIds)
// -- so its own mount IS "the moment of mutual reveal" from this
// user's perspective, not an arbitrary place to fire a celebration.
// Founder, 2026-09-16: "there's a spark" as the product's own language
// for this moment, "should be an animation... not just an emoji...
// maybe with a lightning effect, like a slight jolt? with vibration?" --
// the badge scales up AND rotates in from a slight tilt using "bouncy"
// (tamagui.config.ts's own celebratory/emphasis preset, built for
// exactly this: "success states, confirmations") -- the spring's own
// overshoot IS the jolt, not a separate multi-keyframe shake, which
// keeps this "one authored moment" rather than several stacked
// effects. expo-haptics' notificationAsync (a real, cross-platform
// primitive -- native taptic engine on iOS/Android, and a genuine web
// implementation via navigator.vibrate/an iOS-Safari switch-toggle
// trick, confirmed by reading node_modules/expo-haptics/src/
// ExpoHaptics.web.ts, not assumed) fires alongside it; both are gated
// on prefersReducedMotion, since a vibration is itself a motion effect
// for someone who's asked the OS to reduce them.
function NewConnectionRow({ request }: { request: RevealRequest }) {
  const router = useRouter();
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;
  const prefersReducedMotion = usePrefersReducedMotion();
  const [sparked, setSparked] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setSparked(true);
      if (!prefersReducedMotion) void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    });
    return () => cancelAnimationFrame(frame);
  }, [prefersReducedMotion]);

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() =>
        router.push({
          pathname: '/safety-reminder',
          params: { otherUserId: request.recipientId, connectionId: request.connectionId ?? '' },
        })
      }
    >
      <SolidSurface style={{ padding: space[4] }}>
        <XStack alignItems="center" gap="$3">
          <View position="relative">
            <PseudonymousAvatar id={request.recipientId} size={40} />
            <View
              position="absolute"
              bottom={-2}
              right={-2}
              width={18}
              height={18}
              borderRadius="$full"
              backgroundColor="$primary"
              borderWidth={borderWidth.control}
              borderColor="$card"
              alignItems="center"
              justifyContent="center"
              transition={prefersReducedMotion ? undefined : 'bouncy'}
              scale={prefersReducedMotion || sparked ? 1 : 0}
              rotate={prefersReducedMotion || sparked ? '0deg' : '-25deg'}
            >
              <LightningIcon size={16} strokeWidth={1.75} color={colors.textOnAction} fill={colors.textOnAction} />
            </View>
          </View>
          {/* Founder, 2026-09-16: "there's a spark cannot show a
              safety warning right at the same moment -- not how
              delight works." The copy used to pair "spark" and
              "safety reminder" in the same sentence, undercutting
              both -- the excited beat and the serious one now stay
              separate: this line celebrates only, and the safety-
              reminder screen (still the mandatory next tap, issue
              #16's own hard requirement) carries its own framing once
              you're actually there. */}
          <Text color="$feedbackSuccessText" flex={1} fontWeight="$semibold">
            There&apos;s a spark! Tap to see what&apos;s next.
          </Text>
        </XStack>
      </SolidSurface>
    </Pressable>
  );
}

function OutgoingRequestRow({ request, currentUserId }: { request: RevealRequest; currentUserId: string | undefined }) {
  const status = currentUserId ? describeRevealRequestStatus(request, currentUserId) : undefined;
  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$2">
        {/* Same status Badge as profile-view's ProfileRevealStatus for
            this exact request -- previously this row showed only the
            detail sentence, never the label profile-view already
            rendered for the same request, so the two screens disagreed
            on whether a status marker exists here at all (issue #150's
            dedup gap). Also gives this list its "one differentiator"
            (components.md's composition rule) instead of a wall of
            identical unlabeled cards. */}
        {status && <Badge variant={status.variant}>{status.label}</Badge>}
        <Text color="$color">{status?.detail ?? 'Waiting for a response.'}</Text>
      </YStack>
    </SolidSurface>
  );
}

// Resolved (declined) requests this user sent -- shown separately from
// the "Sent by you" pending list above so the cooldown wait time
// (describeCooldown) has somewhere real to render: it tells a requester
// who was just declined how long until the backend will accept another
// request to the same recipient (reveal_request.go's declineCooldown),
// rather than letting them assume an immediate retry works and hit a
// 429.
function DeclinedRequestRow({ request, currentUserId }: { request: RevealRequest; currentUserId: string | undefined }) {
  const status = currentUserId ? describeRevealRequestStatus(request, currentUserId) : undefined;
  const cooldown = describeCooldown(new Date(), request.cooldownUntil);
  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$2">
        {status && <Badge variant={status.variant}>{status.label}</Badge>}
        <Text color="$color">{status?.detail ?? 'They declined this request.'}</Text>
        {cooldown && (
          <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
            {cooldown}
          </Text>
        )}
      </YStack>
    </SolidSurface>
  );
}

function ConnectionRow({
  connectionId,
  otherUserId,
  otherUserHandle,
  onShowUndoToast,
}: {
  connectionId: string;
  otherUserId: string;
  otherUserHandle?: string | null;
  onShowUndoToast: (message: string, onUndo: () => void) => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  // See IncomingRequestRow's swipeCommitTimeoutRef for why this isn't
  // cleared on unmount (issue #253): armed means it commits, unless
  // explicitly undone.
  const [pendingRevoke, setPendingRevoke] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const revokeCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const revoke = useMutation({
    mutationFn: () => api.revokeConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connections() });
      queryClient.invalidateQueries({ queryKey: queryKeys.activity() });
      // Revoke always creates a Block server-side (RevokeConnectionHandler).
      queryClient.invalidateQueries({ queryKey: queryKeys.threads() });
      queryClient.invalidateQueries({ queryKey: queryKeys.thread() });
    },
  });

  // Row disappears the instant the button is pressed -- same
  // optimistic-hide-plus-toast pattern as IncomingRequestRow's swipe
  // path (issue #253).
  if (pendingRevoke) return null;

  return (
    <SolidSurface style={{ padding: space[4] }}>
      <YStack gap="$3">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="View this connection's profile"
          // Bottom hitSlop trimmed to 0: this row sits directly above
          // the destructive "Revoke connection & block" button -- a
          // full 8px hitSlop overlaps its dead zone and invites a
          // catastrophic mis-tap.
          hitSlop={{ top: 8, left: 8, right: 8, bottom: 0 }}
          onPress={() => router.push({ pathname: '/users/[userId]', params: { userId: otherUserId } })}
        >
          <XStack alignItems="center" gap="$2">
            <PseudonymousAvatar handle={otherUserHandle} id={otherUserId} size={32} />
            <YStack flex={1}>
              <Text color="$color" textAlign="left">
                {otherUserHandle ? displayHandle(otherUserHandle) : 'Connected'}
              </Text>
              <Text color="$colorLink" fontSize="$meta" lineHeight="$meta" textAlign="left">
                View profile
              </Text>
            </YStack>
          </XStack>
        </Pressable>
        {revoke.isError && (
          <Text color="$destructive" fontSize="$meta" lineHeight="$meta">
            {revoke.error.message || 'Something went wrong. Check your connection and try again.'}
          </Text>
        )}
        {/* "Revoke & block" not "Unmatch" -- issue #16's explicit
          acceptance criterion: revoke creates a Block per the data
          model, must not be presented as a soft, reversible action.
          Single confirmation only (issue #17's "no dark patterns"
          rule, applied here too since the consequence is identical
          to a block): the label itself states the real consequence
          up front, no separate "are you sure" step needed on top --
          issue #253 adds the brief undo window this still lacked,
          which is the error-recovery a confirmation modal would have
          given without reintroducing that modal's own friction. */}
        {confirmingRevoke ? (
          <YStack gap="$2">
            <Text color="$color" fontSize="$meta" lineHeight="$meta">
              This disconnects you and blocks this person. They will not be able to find or contact you again.
            </Text>
            <XStack gap="$3">
              <PrimaryButton size="sm" variant="outline" onPress={() => setConfirmingRevoke(false)}>
                Cancel
              </PrimaryButton>
              <PrimaryButton
                size="sm"
                variant="outlineDanger"
                loading={revoke.isPending}
                onPress={() => {
                  if (revoke.isPending || pendingRevoke) return;
                  setConfirmingRevoke(false);
                  setPendingRevoke(true);
                  revokeCommitTimeoutRef.current = setTimeout(() => revoke.mutate(), UNDO_WINDOW_MS);
                  onShowUndoToast('Connection revoked', () => {
                    if (revokeCommitTimeoutRef.current) clearTimeout(revokeCommitTimeoutRef.current);
                    setPendingRevoke(false);
                  });
                }}
              >
                Disconnect and block
              </PrimaryButton>
            </XStack>
          </YStack>
        ) : (
          <PrimaryButton size="sm" variant="outlineDanger" onPress={() => setConfirmingRevoke(true)}>
            Disconnect and block
          </PrimaryButton>
        )}
      </YStack>
    </SolidSurface>
  );
}
